import { useState, useMemo } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import DayCell from '../DayCell/DayCell';
import './CalendarView.css';

// ----------------------------
// Helpers
// ----------------------------
const log = (...args) => console.log('[Kalendarium]', ...args);
const warn = (...args) => console.warn('[Kalendarium]', ...args);
const err = (...args) => console.error('[Kalendarium]', ...args);

// Hilfsfunktion: erzeugt Monatstage + führende/trailing Platzhalter nach weekStartsOn
function getMonthDays(year, month, weekStartsOn = 1) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days = [];

  const firstWeekday = firstDay.getDay(); // 0..6 (So..Sa)
  const leading = (firstWeekday - weekStartsOn + 7) % 7;
  for (let i = 0; i < leading; i++) days.push(null);

  for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));

  const remainder = days.length % 7;
  if (remainder !== 0) {
    const toAdd = 7 - remainder;
    for (let i = 0; i < toAdd; i++) days.push(null);
  }
  return days;
}

// Wochentags-Köpfe passend zum Wochenstart verschieben 0=So,1=Mo
function getWeekdayLabels(locale = 'de-DE', weekStartsOn = 1) {
  const base = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  if (weekStartsOn === 0) return base;
  return [...base.slice(1), base[0]];
}

export default function CalendarView({
  calendar,
  canDelete,
  canWrite,
  onAddEvent,
  onUpdateEvent,
  onDeleteCalendar,
  weekStartsOn = 1, // 1 = Montag (Default), 0 = Sonntag
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const days = useMemo(
    () => getMonthDays(currentMonth.getFullYear(), currentMonth.getMonth(), weekStartsOn),
    [currentMonth, weekStartsOn]
  );
  const monthName = currentMonth.toLocaleString('de-DE', { month: 'long', year: 'numeric' });
  const weekdays = useMemo(() => getWeekdayLabels('de-DE', weekStartsOn), [weekStartsOn]);

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));

  log('Rendering CalendarView for', calendar?.name, 'weekStartsOn=', weekStartsOn, 'canDelete=', canDelete(calendar.id));

  return (
    <div className="calendar-view">
      <div className="calendar-header">
        <button onClick={prevMonth}><FaChevronLeft /></button>
        <h2>{calendar.name} – {monthName}</h2>
        <button onClick={nextMonth}><FaChevronRight /></button>

        {canDelete(calendar.id) && (
          <button
            className="delete-calendar-btn"
            onClick={() => {
              if (confirm(`Kalender "${calendar.name}" wirklich löschen?`)) {
                onDeleteCalendar(calendar.id);
              }
            }}
          >
            Kalender löschen
          </button>
        )}
      </div>

      <div className="calendar-grid">
        {weekdays.map(d => (
          <div key={d} className="weekday">{d}</div>
        ))}
        {days.map((day, index) => (
          <DayCell
            key={index}
            date={day}
            canWrite={canWrite}
            calendar={calendar}
            events={calendar.events || []}
            onAddEvent={(ev) => onAddEvent(ev)}
            onUpdateEvent={(calendarId, event) => onUpdateEvent(calendarId, event)}
          />
        ))}
      </div>
    </div>
  );
}
